import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';

import { useAccount } from '@/hooks/useAccount';
import { IntegrationType } from '@/shared/types/integration';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { addDaysToDate, formatDate } from '@/util/dateUtils';
import { isValidRecipientFormat } from '@/util/validation/patterns';

const CONTACT_SESSIONS_QUERY = gql`
  query ListContactSessions(
    $accountId: uuid
    $channelUserId: String
    $userEmail: String
    $startDate: timestamp
    $endDate: timestamp
    $limit: Int
    $offset: Int
  ) {
    rows: list_sessions(
      args: {
        account_id: $accountId
        channel_user_id: $channelUserId
        user_email: $userEmail
        start_time: $startDate
        end_time: $endDate
        row_limit: $limit
        row_offset: $offset
      }
    ) {
      session_id
      user_name
      start_time
      end_time
      channel
    }
  }
`;

interface ContactSessionRow {
  session_id: string;
  user_name: string | null;
  start_time: string;
  end_time: string;
  channel: string;
}

export interface ContactLookupResult {
  userName: string | null;
  sessionCount: number;
  lastSessionDate: string;
  lastSessionId: string;
}

const DEFAULT_RETENTION_DAYS = 30;

export const useContactLookup = (integrationType: IntegrationType | null, initialTo?: string) => {
  const { account } = useAccount();
  const initialLookupDone = useRef(false);
  const [lookedUpValue, setLookedUpValue] = useState('');

  const dateRange = useMemo(() => {
    const now = new Date();
    const retentionDays = account?.max_log_retention ?? DEFAULT_RETENTION_DAYS;
    const earliest = addDaysToDate(now, -retentionDays);

    return getAnalyticsRangeLocal(
      formatDate(earliest, 'yyyy-MM-dd'),
      formatDate(now, 'yyyy-MM-dd')
    );
  }, [account?.max_log_retention]);

  const [executeQuery, { data, loading, called }] = useLazyQuery<
    { rows: ContactSessionRow[] },
    Record<string, unknown>
  >(CONTACT_SESSIONS_QUERY, { fetchPolicy: 'network-only' });

  const buildVariables = useCallback(
    (value: string) => {
      const isEmail = integrationType === IntegrationType.EMAIL;
      const searchValue = isEmail ? value : value.replace(/^\+/, '');

      return {
        accountId: account?.account_id,
        channelUserId: isEmail ? undefined : searchValue,
        userEmail: isEmail ? searchValue : undefined,
        ...dateRange,
        limit: 50,
        offset: 0,
      };
    },
    [account?.account_id, integrationType, dateRange]
  );

  const lookup = useCallback(
    (value: string): boolean => {
      if (!value || !account?.account_id) {
        return false;
      }
      const recipientType = integrationType === IntegrationType.EMAIL ? 'email' : 'phone';
      if (!isValidRecipientFormat(recipientType, value)) {
        return false;
      }
      setLookedUpValue(value);
      executeQuery({ variables: buildVariables(value) });
      return true;
    },
    [account?.account_id, integrationType, buildVariables, executeQuery]
  );

  // Auto-lookup when modal opens with initialTo pre-filled
  useEffect(() => {
    if (initialTo && !initialLookupDone.current) {
      if (lookup(initialTo)) {
        initialLookupDone.current = true;
      }
    }
  }, [initialTo, lookup]);

  const [result, setResult] = useState<ContactLookupResult | null>(null);
  const [lookupComplete, setLookupComplete] = useState(false);

  useEffect(() => {
    const rows = data?.rows;
    if (!rows || rows.length === 0) {
      setResult(null);
    } else {
      const latest = rows[0];
      setResult({
        userName: latest.user_name,
        sessionCount: rows.length,
        lastSessionDate: latest.end_time || latest.start_time,
        lastSessionId: latest.session_id,
      });
    }
    if (called && !loading) {
      setLookupComplete(true);
    }
  }, [data, called, loading]);

  const reset = useCallback(() => {
    setLookedUpValue('');
    setResult(null);
    setLookupComplete(false);
  }, []);

  return { lookup, reset, result, loading, lookupComplete, lookedUpValue };
};
